import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Kpis, Panel, BarsH, Stack100 } from '../../components/charts';

/* ============================================================
   Dashboard cost.

   Satu angka di halaman ini yang paling penting dan paling mudah
   luput: nilai yang TERTAHAN. Uang yang tidak disertifikasi karena
   pekerjaannya belum diverifikasi atau gambarnya belum IFC bukan
   angka nol — ia angka yang seseorang perlu jelaskan.
   ============================================================ */

export type Summary = {
  contracts: {
    id: string;
    contractNo: string;
    title: string;
    contractor: string;
    currency: string;
    contractSum: number;
    status: string;
    boqItems: number;
    certifiedToDate: number;
    heldValue: number;
  }[];
  totals: {
    budget: number;
    approvedVariations: number;
    certified: number;
    held: number;
    verifiedPct: number;
    fac: number;
    variance: number;
    variancePct: number | null;
  };
  evm: { pv: number; ev: number; ac: number; spi: number; cpi: number; eac: number } | null;
};

/** Ringkas, karena nilai kontrak konstruksi tidak muat di kolom. */
export function money(v: number, currency = 'IDR'): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)} T`;
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)} M`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)} jt`;
  return `${sign}${abs.toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
}

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Summary>(`/cost/summary?projectId=${projectId}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the cost summary.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const { totals, evm, contracts } = data;
  const uncommitted = Math.max(0, totals.budget - totals.certified - totals.held);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Cost Assurance</h1>
          <p>
            Nilai yang disertifikasi hanya menggambarkan pekerjaan yang benar-benar ada, karena
            kuantitasnya datang dari verifikasi lapangan.
          </p>
        </div>
      </div>

      {contracts.length === 0 ? (
        <Empty
          title="Belum ada kontrak"
          body="Buat kontrak dan masukkan BOQ-nya. Sertifikasi menggantung pada item BOQ yang tertaut ke aktivitas."
          action={
            <button className="btn btn-primary" onClick={() => nav('/contracts')}>
              Buka Kontrak
            </button>
          }
        />
      ) : (
        <>
          <Kpis
            items={[
              { label: 'Nilai kontrak', value: money(totals.budget) },
              {
                label: 'Tersertifikasi',
                value: money(totals.certified),
                note: `${totals.verifiedPct.toFixed(1)}% terverifikasi di lapangan`,
              },
              {
                label: 'Tertahan',
                value: money(totals.held),
                note: totals.held > 0 ? 'belum diverifikasi atau gambar belum IFC' : 'tidak ada',
                tone: totals.held > 0 ? 'warn' : 'good',
              },
              {
                label: 'Forecast at completion',
                value: money(totals.fac),
                note:
                  totals.variancePct == null
                    ? undefined
                    : `${totals.variance >= 0 ? '+' : '−'}${Math.abs(totals.variancePct).toFixed(1)}% terhadap kontrak`,
                tone: totals.variance > 0 ? 'crit' : 'good',
              },
            ]}
          />

          <div className="grid-2">
            <Panel
              title="Posisi nilai kontrak"
              note="Tersertifikasi adalah satu-satunya bagian yang mewakili pekerjaan terverifikasi."
            >
              <Stack100
                slices={[
                  { name: 'Tersertifikasi', value: totals.certified, color: 'var(--series-1)' },
                  { name: 'Tertahan', value: totals.held, color: 'var(--series-2)' },
                  { name: 'Belum tersertifikasi', value: uncommitted, color: 'var(--text-muted)' },
                ]}
              />
              {totals.approvedVariations !== 0 && (
                <p className="panel-foot">
                  Variation order yang disetujui: {money(totals.approvedVariations)}. Angka ini ikut
                  naik ke forecast, bukan ke nilai kontrak awal.
                </p>
              )}
            </Panel>

            {evm ? (
              <Panel title="Earned value" note="EV dihitung dari progres terverifikasi, bukan dari klaim.">
                <Kpis
                  items={[
                    { label: 'PV', value: money(evm.pv) },
                    { label: 'EV', value: money(evm.ev) },
                    { label: 'AC', value: money(evm.ac) },
                  ]}
                />
                <div className="filters" style={{ marginTop: 12, marginBottom: 0 }}>
                  <span className={`chip ${evm.spi < 1 ? 'warn' : 'good'}`}>
                    SPI {evm.spi.toFixed(2)}
                  </span>
                  <span className={`chip ${evm.cpi < 1 ? 'crit' : 'good'}`}>
                    CPI {evm.cpi.toFixed(2)}
                  </span>
                  <span className="chip">EAC {money(evm.eac)}</span>
                </div>
              </Panel>
            ) : (
              <Panel title="Earned value" note="Belum dihitung untuk periode mana pun.">
                <p className="hint">
                  EVM memerlukan baseline programme dan satu periode dengan progres terverifikasi.
                  Hitung dari halaman kontrak setelah pengukuran selesai.
                </p>
              </Panel>
            )}
          </div>

          {contracts.some((c) => c.certifiedToDate > 0 || c.heldValue > 0) && (
            <Panel title="Per kontrak" note="Tersertifikasi terhadap nilai kontraknya." wide>
              <BarsH
                againstLabel="Nilai kontrak"
                valueLabel="Tersertifikasi"
                rows={contracts.map((c) => ({
                  label: c.contractNo,
                  value: c.certifiedToDate,
                  against: c.contractSum,
                }))}
              />
            </Panel>
          )}

          <Panel title="Kontrak" note="Klik untuk membuka BOQ, pengukuran, dan sertifikatnya." wide>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Nomor</th>
                    <th>Kontraktor</th>
                    <th>Nilai</th>
                    <th>Tersertifikasi</th>
                    <th>Tertahan</th>
                    <th>BOQ</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id} onClick={() => nav(`/contracts/${c.id}`)}>
                      <td className="num">
                        {c.contractNo}
                        <div className="hint">{c.title}</div>
                      </td>
                      <td>{c.contractor}</td>
                      <td>{money(c.contractSum, c.currency)}</td>
                      <td>{money(c.certifiedToDate, c.currency)}</td>
                      <td className={c.heldValue > 0 ? 'neg' : ''}>
                        {c.heldValue > 0 ? money(c.heldValue, c.currency) : '—'}
                      </td>
                      <td>{c.boqItems}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}
